import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Screen } from '../components/Screen'
import { useLeaveGuard } from '../components/Form'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { matchesQuery } from '../data/athletes'
import { addedAthletes } from '../data/events'
import { useAthletes, useEvent, useEventParticipants, useGroups, useInvalidateEvent, useSingleFlight } from '../data/hooks'
import { pluralAthletes } from '../data/schedule'
import type { Athlete, GroupRef } from '../data/types'
import { liveGroupId } from '../../utils/liveGroup'
import { useBackTo } from './AthleteScreen'
import { eventAddedKey, type EventAddedNotice } from './EventScreens'

const NO_GROUP = '__none__'

/**
 * «Добавить участников» (events.md, раздел 4). Типичный случай - вся группа
 * без пары человек: флажок группы отмечает всех, кого ещё нет в событии, и
 * раскрывает состав, чтобы снять лишних. Кто уже в списке - отмечен и
 * недоступен. Добавление - после шторки с точным списком; повтор безопасен.
 * ?g= - группа, отмеченная заранее (вход из группы), ?q= - поиск.
 */
export function EventAddScreen() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { api } = useEnv()
  const client = useQueryClient()
  const backTo = useBackTo(`/schedule/events/${id}`)
  const event = useEvent(id)
  const parts = useEventParticipants(id)
  const athletes = useAthletes()
  const groups = useGroups()
  const invalidate = useInvalidateEvent()
  const flight = useSingleFlight()
  const query = params.get('q') ?? ''
  const preGroup = params.get('g')

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState<Set<string>>(new Set(preGroup ? [preGroup] : []))
  const [confirm, setConfirm] = useState(false)
  const [failed, setFailed] = useState(false)
  const leave = useLeaveGuard(selected.size > 0 && !flight.busy)
  const back = () => leave.guard(backTo)

  const joined = useMemo(() => new Set((parts.data ?? []).map((p) => p.athleteId)), [parts.data])
  const sections = useMemo(() => groupSections(athletes.data ?? [], groups.data), [athletes.data, groups.data])

  // Вход из группы: её состав отмечен сразу (кроме уже записанных), один раз.
  const preselected = useRef(false)
  useEffect(() => {
    if (preselected.current || !preGroup || !athletes.data || !parts.data || !groups.data) return
    preselected.current = true
    const ids = (sections.find((s) => s.id === preGroup)?.athletes ?? []).map((a) => a.id).filter((a) => !joined.has(a))
    setSelected(new Set(ids))
  }, [preGroup, athletes.data, parts.data, groups.data, sections, joined])

  const toggle = (athleteId: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(athleteId)) next.delete(athleteId)
      else next.add(athleteId)
      return next
    })

  const toggleGroup = (sectionId: string, free: string[]) => {
    const all = free.length > 0 && free.every((a) => selected.has(a))
    setSelected((prev) => {
      const next = new Set(prev)
      for (const a of free) {
        if (all) next.delete(a)
        else next.add(a)
      }
      return next
    })
    // Отметили группу - состав раскрывается: сразу видно, кого снять.
    if (!all) setOpen((prev) => new Set(prev).add(sectionId))
  }

  // Добавление прошло: экран уходит на карточку, второе нажатие ничего не шлёт
  // (иначе повтор вернул бы «все уже в списке» и перетёр итог).
  const done = useRef(false)
  const add = async () => {
    if (done.current) return
    setFailed(false)
    const ids = [...selected]
    try {
      const res = await flight.run(() => api.addEventParticipants(id, ids))
      if (!res) return
      done.current = true
      const names = new Map((athletes.data ?? []).map((a) => [a.id, a.name]))
      const notice: EventAddedNotice = {
        added: res.added.map((a) => ({ id: a, name: names.get(a) ?? 'Спортсмен' })),
        already: res.already.length,
        failed: res.failed,
      }
      client.setQueryData(eventAddedKey(id), notice)
      await invalidate(id)
      setSelected(new Set())
      setConfirm(false)
      // Итог - на карточке события (плашка «Добавлено» с отменой). Из
      // карточки пришли - назад к ней; из группы - на карточку вместо экрана.
      if (preGroup) navigate(`/schedule/events/${id}`, { replace: true })
      else backTo()
    } catch {
      setFailed(true)
    }
  }

  if (event.isError) {
    return (
      <Screen title="Добавить участников" back={backTo}>
        <ErrorState title="Не удалось открыть событие" onRetry={() => void event.refetch()} />
      </Screen>
    )
  }

  const loading = athletes.isPending || groups.isPending || parts.isPending
  const loadError = athletes.isError || groups.isError || parts.isError
  const chosen = (athletes.data ?? []).filter((a) => selected.has(a.id))
  const groupName = new Map((groups.data ?? []).map((g) => [g.id, g.name]))

  let body
  if (loading) body = <Skeleton rows={4} label="Загружаю спортсменов" />
  else if (loadError) {
    body = (
      <ErrorState
        title="Не удалось загрузить спортсменов"
        onRetry={() => {
          void athletes.refetch()
          void groups.refetch()
          void parts.refetch()
        }}
      />
    )
  } else if ((athletes.data ?? []).length === 0) {
    body = <EmptyState title="Спортсменов пока нет" text="Добавьте спортсменов в разделе «Спортсмены»." />
  } else if (query.trim()) {
    const found = (athletes.data ?? []).filter((a) => matchesQuery(a, query))
    body =
      found.length === 0 ? (
        <EmptyState title="Никого не нашлось" text={`По запросу «${query.trim()}» спортсменов нет.`} />
      ) : (
        <ul className="pc-list pc-card" style={{ padding: '0 12px', marginTop: 12 }}>
          {found.map((a) => {
            const gid = liveGroupId(a.group_id, groups.data)
            return (
              <li key={a.id}>
                <AthleteCheck
                  athlete={a}
                  meta={gid ? groupName.get(gid) : 'Без группы'}
                  joined={joined.has(a.id)}
                  checked={selected.has(a.id)}
                  onToggle={() => toggle(a.id)}
                />
              </li>
            )
          })}
        </ul>
      )
  } else {
    body = sections.map((s) => {
      const free = s.athletes.filter((a) => !joined.has(a.id)).map((a) => a.id)
      const picked = free.filter((a) => selected.has(a)).length
      const expanded = open.has(s.id)
      return (
        <section key={s.id} className="pc-card pc-group-pick" aria-label={s.name}>
          <div className="pc-group-pick__head">
            <GroupCheck
              name={s.name}
              total={s.athletes.length}
              free={free.length}
              picked={picked}
              onToggle={() => toggleGroup(s.id, free)}
            />
            <button
              type="button"
              className="pc-icon-btn"
              style={{ border: 0 }}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Скрыть' : 'Показать'} состав: ${s.name}`}
              onClick={() =>
                setOpen((prev) => {
                  const next = new Set(prev)
                  if (next.has(s.id)) next.delete(s.id)
                  else next.add(s.id)
                  return next
                })
              }
            >
              {expanded ? <ChevronUp size={22} aria-hidden="true" /> : <ChevronDown size={22} aria-hidden="true" />}
            </button>
          </div>
          {expanded && (
            <ul className="pc-list pc-group-pick__list">
              {s.athletes.map((a) => (
                <li key={a.id}>
                  <AthleteCheck
                    athlete={a}
                    joined={joined.has(a.id)}
                    checked={selected.has(a.id)}
                    onToggle={() => toggle(a.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      )
    })
  }

  return (
    <Screen
      title="Добавить участников"
      subtitle={event.data?.name}
      back={back}
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={selected.size === 0}
          onClick={() => {
            setFailed(false)
            setConfirm(true)
          }}
        >
          {selected.size === 0 ? 'Отметьте, кого добавить' : `Добавить: ${selected.size}`}
        </button>
      }
    >
      <div className="pc-search">
        <label className="pc-visually-hidden" htmlFor="pc-ev-search">
          Поиск по имени
        </label>
        <input
          id="pc-ev-search"
          className="pc-input"
          type="search"
          placeholder="Поиск по имени во всех группах"
          value={query}
          onChange={(e) => {
            const p = new URLSearchParams(params)
            if (e.target.value) p.set('q', e.target.value)
            else p.delete('q')
            setParams(p, { replace: true })
          }}
          autoComplete="off"
        />
      </div>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        Флажок группы отмечает всех, кого ещё нет в событии. Лишних можно снять.
      </p>
      {body}

      {confirm && (
        <Sheet title={`Добавить в «${event.data?.name ?? 'событие'}»?`} onClose={() => setConfirm(false)}>
          <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
            {addedAthletes(chosen.length)}, статус - «В заявке»:
          </p>
          <ul className="pc-list" style={{ marginBottom: 12 }}>
            {chosen.map((a) => {
              const gid = liveGroupId(a.group_id, groups.data)
              return (
                <li key={a.id} className="pc-kv">
                  <div>{a.name}</div>
                  <div className="pc-kv__value">{gid ? groupName.get(gid) : 'Без группы'}</div>
                </li>
              )
            })}
          </ul>
          {failed && <SaveFailedAlert>Повтор безопасен: кто уже добавлен, второй раз не добавится.</SaveFailedAlert>}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--primary"
            style={{ marginTop: 12 }}
            disabled={flight.busy}
            onClick={() => void add()}
          >
            {flight.busy ? 'Добавляю…' : failed ? 'Повторить' : `Добавить ${chosen.length}`}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirm(false)}>
            Вернуться к выбору
          </button>
        </Sheet>
      )}
      {leave.dialog}
    </Screen>
  )
}

interface Section {
  id: string
  name: string
  athletes: Athlete[]
}

/** Группы по алфавиту списка групп, внутри - по имени; спортсмены без группы - последним разделом. */
function groupSections(athletes: Athlete[], groups: GroupRef[] | undefined): Section[] {
  const sorted = [...athletes].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
  const out: Section[] = (groups ?? []).map((g) => ({
    id: g.id,
    name: g.name,
    athletes: sorted.filter((a) => liveGroupId(a.group_id, groups) === g.id),
  }))
  const none = sorted.filter((a) => !liveGroupId(a.group_id, groups))
  if (none.length > 0) out.push({ id: NO_GROUP, name: 'Без группы', athletes: none })
  return out.filter((s) => s.athletes.length > 0)
}

function GroupCheck({
  name,
  total,
  free,
  picked,
  onToggle,
}: {
  name: string
  total: number
  free: number
  picked: number
  onToggle: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const all = free > 0 && picked === free
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = picked > 0 && picked < free
  }, [picked, free])
  const meta =
    free === 0
      ? total === 1
        ? 'уже в списке'
        : `все ${total} уже в списке`
      : picked > 0
        ? `выбрано ${picked} из ${free}${free < total ? `, в списке уже ${total - free}` : ''}`
        : `${pluralAthletes(total)}${free < total ? `, в списке уже ${total - free}` : ''}`
  return (
    <label className="pc-check-row" data-disabled={free === 0 || undefined}>
      <input
        ref={ref}
        type="checkbox"
        checked={all || free === 0}
        disabled={free === 0}
        onChange={onToggle}
        aria-label={`Вся группа «${name}»`}
      />
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {name}
        </span>
        <span className="pc-list-row__meta">{meta}</span>
      </span>
    </label>
  )
}

function AthleteCheck({
  athlete: a,
  meta,
  joined,
  checked,
  onToggle,
}: {
  athlete: Athlete
  meta?: string
  joined: boolean
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label className="pc-check-row" data-disabled={joined || undefined}>
      <input type="checkbox" checked={joined || checked} disabled={joined} onChange={onToggle} />
      <span className="pc-list-row__main">
        <span className="pc-list-row__title" style={{ display: 'block' }}>
          {a.name}
        </span>
        {(joined || meta) && <span className="pc-list-row__meta">{[meta, joined ? 'Уже в списке' : null].filter(Boolean).join(' / ')}</span>}
      </span>
    </label>
  )
}
